import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import client from "@/api/client";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, GripVertical, RotateCcw, Save, ListChecks } from "lucide-react";

const ROLES = ["founder", "mentor", "investor", "enabler"];

/**
 * AdminOnboardingPlan — designs the manual steps a new member sees in their
 * dashboard checklist. Automatic steps (profile, phone, company, first task)
 * are computed by the platform and are not editable here.
 */
export function AdminOnboardingPlan() {
  const { t } = useTranslation();
  const [role, setRole] = useState("founder");
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ customised: false, updated_at: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get(`/admin/onboarding-plan?role=${role}`);
      setItems(res.data?.items || []);
      setMeta({ customised: !!res.data?.customised, updated_at: res.data?.updated_at || "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [role, t]);
  useEffect(() => { load(); }, [load]);

  const setField = (idx, field, value) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };
  const addItem = () => setItems((prev) => [...prev, { item_id: "", title: "", description: "" }]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    setItems((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const save = async () => {
    const clean = items
      .filter((i) => (i.title || "").trim())
      .map((i) => ({
        item_id: i.item_id || "",
        title: i.title,
        title_ar: i.title_ar || "",
        description: i.description || "",
        description_ar: i.description_ar || "",
      }));
    setSaving(true);
    try {
      const res = await client.put("/admin/onboarding-plan", { role, items: clean });
      setItems(res.data?.items || []);
      setMeta({ customised: true, updated_at: res.data?.updated_at || "" });
      toast.success(t("onboardingPlan.saved", { defaultValue: "Onboarding plan saved" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      const res = await client.delete(`/admin/onboarding-plan/${role}`);
      setItems(res.data?.items || []);
      setMeta({ customised: false, updated_at: "" });
      toast.success(t("onboardingPlan.reset", { defaultValue: "Reset to the built-in plan" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-onboarding-plan">
      <div>
        <h2 className="font-display text-lg font-semibold flex items-center gap-2">
          <ListChecks className="h-4 w-4 text-primary" />
          {t("onboardingPlan.title", { defaultValue: "Onboarding plan" })}
        </h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          {t("onboardingPlan.subtitle", { defaultValue: "Design the steps new members tick off on their dashboard. Profile, phone, company and first-task steps complete themselves automatically." })}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {ROLES.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setRole(r)}
            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-all ${
              role === r ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:border-primary/40 hover:text-primary"
            }`}
            data-testid={`onboarding-plan-role-${r}`}
          >
            {t(`roles.${r}`, { defaultValue: r })}
          </button>
        ))}
        <Badge variant="outline" className="rounded-full text-[10px] ms-auto" data-testid="onboarding-plan-state">
          {meta.customised
            ? t("onboardingPlan.custom", { defaultValue: "Customised" })
            : t("onboardingPlan.builtIn", { defaultValue: "Built-in default" })}
        </Badge>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
      ) : (
        <Card className="rounded-2xl border border-border p-4 space-y-3" data-testid="onboarding-plan-items">
          {items.length === 0 && (
            <p className="text-sm text-muted-foreground py-6 text-center">
              {t("onboardingPlan.empty", { defaultValue: "No manual steps — members only see the automatic ones." })}
            </p>
          )}
          {items.map((it, idx) => (
            <div key={it.item_id || `new-${idx}`} className="flex items-start gap-2 rounded-xl border border-border p-2.5" data-testid={`onboarding-plan-item-${idx}`}>
              <div className="flex flex-col items-center gap-0.5 pt-1.5">
                <button type="button" onClick={() => move(idx, -1)} className="text-muted-foreground hover:text-primary text-[10px]" aria-label="Move up" data-testid={`onboarding-plan-up-${idx}`}>▲</button>
                <GripVertical className="h-3 w-3 text-muted-foreground/50" />
                <button type="button" onClick={() => move(idx, 1)} className="text-muted-foreground hover:text-primary text-[10px]" aria-label="Move down" data-testid={`onboarding-plan-down-${idx}`}>▼</button>
              </div>
              <div className="min-w-0 flex-1 space-y-2">
                <Input
                  value={it.title || ""}
                  onChange={(e) => setField(idx, "title", e.target.value)}
                  placeholder={t("onboardingPlan.titlePlaceholder", { defaultValue: "Step title" })}
                  className="rounded-lg h-9"
                  data-testid={`onboarding-plan-title-${idx}`}
                />
                <Input
                  value={it.description || ""}
                  onChange={(e) => setField(idx, "description", e.target.value)}
                  placeholder={t("onboardingPlan.descPlaceholder", { defaultValue: "Short helper text (optional)" })}
                  className="rounded-lg h-9 text-xs"
                  data-testid={`onboarding-plan-desc-${idx}`}
                />
                <div className="grid gap-2 sm:grid-cols-2" dir="rtl">
                  <Input
                    value={it.title_ar || ""}
                    onChange={(e) => setField(idx, "title_ar", e.target.value)}
                    placeholder={t("onboardingPlan.titleArPlaceholder", { defaultValue: "عنوان الخطوة (عربي)" })}
                    className="rounded-lg h-9 text-xs"
                    data-testid={`onboarding-plan-title-ar-${idx}`}
                  />
                  <Input
                    value={it.description_ar || ""}
                    onChange={(e) => setField(idx, "description_ar", e.target.value)}
                    placeholder={t("onboardingPlan.descArPlaceholder", { defaultValue: "نص توضيحي (عربي)" })}
                    className="rounded-lg h-9 text-xs"
                    data-testid={`onboarding-plan-desc-ar-${idx}`}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeItem(idx)}
                className="text-muted-foreground hover:text-destructive mt-1.5"
                aria-label="Remove step"
                data-testid={`onboarding-plan-remove-${idx}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}

          <div className="flex flex-wrap gap-2 pt-1">
            <Button variant="outline" size="sm" className="rounded-xl gap-1.5" onClick={addItem} data-testid="onboarding-plan-add">
              <Plus className="h-3.5 w-3.5" /> {t("onboardingPlan.add", { defaultValue: "Add step" })}
            </Button>
            <Button size="sm" className="btn-brand rounded-xl gap-1.5" onClick={save} disabled={saving} data-testid="onboarding-plan-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              {t("common.save", { defaultValue: "Save" })}
            </Button>
            {meta.customised && (
              <Button variant="ghost" size="sm" className="rounded-xl gap-1.5 ms-auto" onClick={reset} disabled={saving} data-testid="onboarding-plan-reset">
                <RotateCcw className="h-3.5 w-3.5" /> {t("onboardingPlan.resetCta", { defaultValue: "Reset to default" })}
              </Button>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}

export default AdminOnboardingPlan;
